import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ShieldAlertIcon } from 'lucide-react';
import { app, BridgeError, notes, type NoteAction, type ShushuPost } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { useNoteActions } from '@/lib/useNoteActions';
import { Segmented } from '@/components/Segmented';
import { PageShell } from '@/components/PageShell';
import { InfoCard, TaskProgressCard } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CARD } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 已发表内容（完整列表）。
 *
 * 主页只露最近几篇，完整列表在这里；分「已发布 / 已隐藏 / 已删除」三组（已删除有才显示）。
 * 点一行进笔记详情（原生推新页）；「选择」后底部按选中项渲染可做的操作（原生 `notes.actions` 下发，
 * 带适用条数），执行走 `useNoteActions`。数据来自 App 本地库（`notes.posts`），平台无关：
 * 不按平台名写分支，页面标题（笔记 / 作品）由原生导航栏给。
 */

type Segment = 'published' | 'hidden' | 'deleted';

/** 列表行的指标：有才显示。 */
const METRICS: [string, string][] = [
  ['views', '浏览'],
  ['likes', '点赞'],
  ['comments', '评论'],
  ['collects', '收藏'],
];

function compact(n: number): string {
  if (Math.abs(n) >= 10_000) return `${(n / 10_000).toFixed(1)}万`;
  return `${Math.round(n)}`;
}

/** 毫秒 → `2026/09/28` */
function formatDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

export function ShushuNotesPage() {
  const navigate = useNavigate();
  const { loading: infoLoading, isNative } = useAppInfo();
  const { selectedId, error: accountError, loading: accountsLoading } = useAccounts();
  const [posts, setPosts] = useState<ShushuPost[] | null>(null);
  const [segment, setSegment] = useState<Segment>('published');
  const [error, setError] = useState<string | null>(null);
  const [outdated, setOutdated] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** 账号支持的操作（空 ids 取目录，只看有没有） */
  const [catalog, setCatalog] = useState<NoteAction[]>([]);
  /** 当前选中项的操作（带适用条数） */
  const [selectionActions, setSelectionActions] = useState<NoteAction[]>([]);
  const [reloadTick, setReloadTick] = useState(0);

  const loaded =
    !infoLoading && !accountsLoading && (!selectedId || posts !== null || outdated || error !== null);
  useAppReady(loaded);
  useGroupedSurface();

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    Promise.all([notes.posts(selectedId, { includeDeleted: true }), notes.actions(selectedId, []).catch(() => ({ actions: [] }))])
      .then(([result, actions]) => {
        if (cancelled) return;
        setPosts(result.items);
        setCatalog(actions.actions);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof BridgeError && e.code === 'unknown_method') setOutdated(true);
        else setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, reloadTick]);

  // 操作结束：退出选择、刷新列表（可见性 / 删除状态变了，条目会换组）
  const onFinished = useCallback(() => {
    setSelecting(false);
    setSelected(new Set());
    setReloadTick((t) => t + 1);
  }, []);
  const { snapshot, running, busy, error: runError, run } = useNoteActions(selectedId, onFinished);

  const { published, hidden, deleted } = useMemo(() => {
    const all = posts ?? [];
    return {
      published: all.filter((p) => !p.deleted && p.visibility !== 'private'),
      hidden: all.filter((p) => !p.deleted && p.visibility === 'private'),
      deleted: all.filter((p) => p.deleted),
    };
  }, [posts]);
  const visible = segment === 'published' ? published : segment === 'hidden' ? hidden : deleted;
  const allSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));
  const canSelect = catalog.length > 0 && segment !== 'deleted';

  // 选中项变化 → 重新取可做的操作（原生按每篇状态算适用条数）
  useEffect(() => {
    if (!selecting || !selectedId || selected.size === 0) {
      setSelectionActions([]);
      return;
    }
    let cancelled = false;
    notes
      .actions(selectedId, [...selected])
      .then((res) => {
        if (!cancelled) setSelectionActions(res.actions.filter((a) => a.applicable > 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selecting, selectedId, selected]);

  const switchSegment = (next: Segment) => {
    setSegment(next);
    setSelected(new Set());
    if (next === 'deleted') setSelecting(false);
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((p) => p.id)));

  /** 进详情：App 内推原生新页（返回键回列表），否则页内跳转 */
  const openDetail = async (post: ShushuPost) => {
    const path = `/shushu/notes/${encodeURIComponent(post.id)}?accountId=${encodeURIComponent(selectedId)}`;
    if (!(await app.openPage(path, '详情'))) navigate(path);
  };

  const segments: [Segment, string][] = [
    ['published', `已发布 ${published.length}`],
    ['hidden', `已隐藏 ${hidden.length}`],
    ...(deleted.length > 0 ? [['deleted', `已删除 ${deleted.length}`] as [Segment, string]] : []),
  ];

  return (
    <PageShell title="已发表内容" description="按可见性查看账号的全部内容">
      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持这个页面，请更新到最新版本后再试。
        </InfoCard>
      ) : null}

      {running && snapshot ? (
        <TaskProgressCard
          title="正在处理"
          right={`${snapshot.done ?? 0} / ${snapshot.total ?? 0}`}
          done={snapshot.done}
          total={snapshot.total}
          detail={snapshot.failed ? `失败 ${snapshot.failed} 篇` : undefined}
        />
      ) : null}
      {snapshot && snapshot.state === 'failed' && snapshot.error ? (
        <p className="text-destructive px-1 text-footnote">{describeError(snapshot.error)}</p>
      ) : null}

      {posts ? (
        <>
          <Segmented value={segment} options={segments} onChange={switchSegment} />

          {canSelect && visible.length > 0 ? (
            <div className="flex items-center justify-between px-1">
              {selecting ? (
                <button type="button" className="text-primary text-subheadline" onClick={toggleAll}>
                  {allSelected ? '取消全选' : '全选'}
                </button>
              ) : (
                <span className="text-muted-foreground text-footnote tabular-nums">共 {visible.length} 篇</span>
              )}
              <button
                type="button"
                disabled={running}
                className="text-primary text-subheadline disabled:opacity-40"
                onClick={() => {
                  setSelecting((v) => !v);
                  setSelected(new Set());
                }}
              >
                {selecting ? '取消' : '选择'}
              </button>
            </div>
          ) : null}

          {visible.length === 0 ? (
            <Card className={CARD}>
              <p className="text-muted-foreground py-6 text-center text-sm">
                {segment === 'published' ? '还没有已发布的内容' : segment === 'hidden' ? '没有已隐藏的内容' : '没有已删除的内容'}
              </p>
              {segment === 'hidden' ? (
                <p className="text-muted-foreground text-caption1 text-center leading-snug">
                  「仅自己可见」的内容会出现在这里；可见性以最近一次同步为准。
                </p>
              ) : null}
            </Card>
          ) : (
            <Card className={cn(CARD, 'gap-0 py-1')}>
              {visible.map((post, index) => {
                const metricLine = METRICS.filter(([key]) => post.metrics[key] != null)
                  .map(([key, label]) => `${label} ${compact(post.metrics[key])}`)
                  .join(' · ');
                return (
                  <div
                    key={post.id}
                    role="button"
                    data-haptic
                    onClick={() => (selecting ? toggle(post.id) : void openDetail(post))}
                    className={cn(
                      'flex min-w-0 items-center gap-3 py-3 active:opacity-60',
                      index > 0 && 'border-border border-t',
                    )}
                  >
                    {selecting ? (
                      // 整行是点击区，勾选框只做展示（否则点框会和行各触发一次，抵消）
                      <Checkbox checked={selected.has(post.id)} tabIndex={-1} className="pointer-events-none" />
                    ) : null}
                    {post.cover ? (
                      <img
                        src={post.cover}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className={cn('bg-muted size-11 shrink-0 rounded-md object-cover', post.deleted && 'opacity-50')}
                        onError={(e) => {
                          e.currentTarget.style.visibility = 'hidden';
                        }}
                      />
                    ) : (
                      <div className="bg-muted size-11 shrink-0 rounded-md" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={cn('text-subheadline truncate ios:text-body', post.deleted && 'text-muted-foreground')}>
                        {post.title || '无标题'}
                      </div>
                      <div className="text-caption1 text-muted-foreground truncate tabular-nums">
                        {[
                          post.publishedAt ? formatDate(post.publishedAt) : null,
                          post.deleted ? (post.backedUp ? '本机已备份' : '未备份') : metricLine || null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    {selecting ? null : <ChevronRightIcon className="text-muted-foreground/50 size-4 shrink-0" />}
                  </div>
                );
              })}
            </Card>
          )}
        </>
      ) : null}

      {(error ?? runError ?? accountError) ? (
        <p className="text-destructive px-1 text-sm">{error ?? runError ?? accountError}</p>
      ) : null}

      {/* 选择模式的底部操作栏：按选中项渲染可做的操作（带适用条数），删除红色 */}
      {selecting ? (
        <div className="bg-background/90 fixed inset-x-0 bottom-0 z-10 border-t px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)] backdrop-blur">
          {selected.size === 0 ? (
            <p className="text-muted-foreground py-3 text-center text-subheadline">选择要操作的内容</p>
          ) : selectionActions.length === 0 ? (
            <p className="text-muted-foreground py-3 text-center text-subheadline">选中的内容没有可做的操作</p>
          ) : (
            <div className="flex gap-3">
              {selectionActions.map((action) => (
                <Button
                  key={action.key}
                  className={cn('flex-1', action.destructive && 'text-destructive')}
                  variant={action.destructive ? 'secondary' : 'default'}
                  loading={busy}
                  disabled={running || !!action.disabledReason}
                  onClick={() => void run(action, [...selected])}
                >
                  {action.label}（{action.applicable}）
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {!loaded ? <PageSkeleton kind="posts" rows={6} /> : null}
    </PageShell>
  );
}
